import React, { useState } from "react";
import Resources from "../../../types/Resources";
import ClickableLabelCheckbox from "../../Common/ClickableLabelCheckbox";
import WorkAtHeightChecklistModal from "./WorkAtHeightChecklistModal";
import ScaffoldingChecklistModal from "./ScaffoldingChecklistModal";
import ScaffoldingConstructionChecklistModal from "./ScaffoldingConstructionChecklistModal";
import ScaffoldingWorkChecklistModal from "./ScaffoldingWorkChecklistModal";
import ClosedSpaceChecklistModal from "./ClosedSpaceChecklistModal";
import AsbestosChecklistModal from "./AsbestosChecklistModal";
import HydrogenSulphideChecklistModal from "./HydrogenSulphideChecklistModal";
import BeefGasChecklistModal from "./BeefGasChecklistModal";
import HeavyLiftingChecklistModal from "./HeavyLiftingChecklistModal";
import ExcavationAndPolingChecklistModal from "./ExcavationAndPolingChecklistModal";
import RaftChecklistModal from "./RaftChecklistModal";
import DivingChecklistModal from "./DivingChecklistModal";
import RiskIdentificationsFormState from "../../../types/forms/Permits/Conditions/RiskIdentificationsFormState";
import WorkAtHeightChecklistFormState from "../../../types/forms/Permits/Conditions/WorkAtHeightChecklistFormState";
import ScaffoldingChecklistFormState from "../../../types/forms/Permits/Conditions/ScaffoldingChecklistFormState";
import ScaffoldingConstructionChecklistFormState from "../../../types/forms/Permits/Conditions/ScaffoldingConstructionChecklistFormState";
import ScaffoldingWorkChecklistFormState from "../../../types/forms/Permits/Conditions/ScaffoldingWorkChecklistFormState";
import ClosedSpaceChecklistFormState from "../../../types/forms/Permits/Conditions/ClosedSpaceChecklistFormState";
import AsbestosChecklistFormState from "../../../types/forms/Permits/Conditions/AsbestosChecklistFormState";
import HydrogenSulphideChecklistFormState from "../../../types/forms/Permits/Conditions/HydrogenSulphideChecklistFormState";
import BeefGasChecklistFormState from "../../../types/forms/Permits/Conditions/BeefGasChecklistFormState";
import { HeavyLiftingChecklistFormState } from "../../../types/forms/Permits/Conditions/HeavyLiftingChecklistFormState";
import ExcavationAndPolingChecklistFormState from "../../../types/forms/Permits/Conditions/ExcavationAndPolingChecklistFormState";
import RaftChecklistFormState from "../../../types/forms/Permits/Conditions/RaftChecklistFormState";
import DivingChecklistFormState from "../../../types/forms/Permits/Conditions/DivingChecklistFormState";
import BaseUserInfo from "../../../types/BaseUserInfo";
import { SignatureManager } from "../../../hooks/useSignature";
import SignatureModal from "../../Signatures/SignatureModal";
import { RiskIdentificationsManager } from "../../../hooks/useRiskIdentificationsUIState";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { RiskIdentifications as RiskIdentificationsType } from "../../../types/permits/conditions/RiskIdentifications";
import { WORK_AT_HEIGHT_CHECKLIST_VERSION_2026_02 } from "../../../types/permits/conditions/WorkAtHeightChecklist";
import { CLOSED_SPACE_CHECKLIST_VERSION_2026_02 } from "../../../types/permits/conditions/ClosedSpaceChecklist";
import { ASBESTOS_CHECKLIST_VERSION_2026_02 } from "../../../types/permits/conditions/AsbestosChecklist";
import { HYDROGEN_SULPHIDE_CHECKLIST_VERSION_2026_02 } from "../../../types/permits/conditions/HydrogenSulphideChecklist";
import { BEEF_GAS_CHECKLIST_VERSION_2026_02 } from "../../../types/permits/conditions/BeefGasChecklist";
import { HEAVY_LIFTING_CHECKLIST_VERSION_2026_02 } from "../../../types/permits/conditions/HeavyLiftingChecklist";
import { EXCAVATION_AND_POLING_CHECKLIST_VERSION_2026_02 } from "../../../types/permits/conditions/ExcavationAndPolingChecklist";

type RiskIdentificationsProps = {
    dbRiskIdentifications: RiskIdentificationsType | undefined;
    resources: Resources;
    formState: RiskIdentificationsFormState;
    baseUserInfo?: BaseUserInfo;
    onChange: (fn: (prevValue: RiskIdentificationsFormState) => RiskIdentificationsFormState) => void;
    uiState: RiskIdentificationsManager;
    craneOperatorSignatureManager?: SignatureManager;
    canManageContent: boolean;
    canCreateUnderWaterWorkPermits: boolean;
    canCreateDivingPermits: boolean;
};

function RiskIdentifications({
    dbRiskIdentifications,
    resources,
    formState,
    onChange,
    baseUserInfo,
    craneOperatorSignatureManager,
    uiState,
    canCreateDivingPermits,
    canCreateUnderWaterWorkPermits,
    canManageContent
}: RiskIdentificationsProps) {
    const {
        setShowAsbestosChecklistModal,
        setShowBeefGasChecklistModal,
        setShowClosedSpaceChecklistModal,
        setShowDivingChecklistModal,
        setShowExcavationAndPolingChecklistModal,
        setShowHeavyLiftingChecklistModal,
        setShowHydrogenSulphideChecklistModal,
        setShowRaftChecklistModal,
        setShowScaffoldingChecklistModal,
        setShowScaffoldingConstructionChecklistModal,
        setShowScaffoldingWorkChecklistModal,
        setShowWorkAtHeightChecklistModal,
        showAsbestosChecklistModal,
        showBeefGasChecklistModal,
        showClosedSpaceChecklistModal,
        showDivingChecklistModal,
        showExcavationAndPolingChecklistModal,
        showHeavyLiftingChecklistModal,
        showHydrogenSulphideChecklistModal,
        showRaftChecklistModal,
        showScaffoldingChecklistModal,
        showScaffoldingConstructionChecklistModal,
        showScaffoldingWorkChecklistModal,
        showWorkAtHeightChecklistModal
    } = uiState;

    function changeHandler(key: keyof RiskIdentificationsFormState, value: any) {
        onChange((prevValue) => {
            const updatedFormState: RiskIdentificationsFormState = {
                ...prevValue,
                [key]: { ...prevValue[key], value }
            };

            return updatedFormState;
        });
    }

    function workAtHeightChecklistChangeHandler(
        checklistChangeHandler: (prevValue: WorkAtHeightChecklistFormState) => WorkAtHeightChecklistFormState
    ) {
        onChange((prevValue) => {
            const updatedFormState: RiskIdentificationsFormState = {
                ...prevValue,
                workAtHeightChecklist: checklistChangeHandler(prevValue.workAtHeightChecklist)
            };

            return updatedFormState;
        });
    }

    function scaffoldingChecklistChangeHandler(
        checklistChangeHandler: (prevValue: ScaffoldingChecklistFormState) => ScaffoldingChecklistFormState
    ) {
        onChange((prevValue) => {
            const updatedFormState: RiskIdentificationsFormState = {
                ...prevValue,
                scaffoldingChecklist: checklistChangeHandler(prevValue.scaffoldingChecklist)
            };

            return updatedFormState;
        });
    }

    function scaffoldingConstructionChecklistChangeHandler(
        checklistChangeHandler: (
            prevValue: ScaffoldingConstructionChecklistFormState
        ) => ScaffoldingConstructionChecklistFormState
    ) {
        onChange((prevValue) => {
            const updatedFormState: RiskIdentificationsFormState = {
                ...prevValue,
                scaffoldingConstructionChecklist: checklistChangeHandler(prevValue.scaffoldingConstructionChecklist)
            };

            return updatedFormState;
        });
    }

    function scaffoldingWorkChecklistChangeHandler(
        checklistChangeHandler: (prevValue: ScaffoldingWorkChecklistFormState) => ScaffoldingWorkChecklistFormState
    ) {
        onChange((prevValue) => {
            const updatedFormState: RiskIdentificationsFormState = {
                ...prevValue,
                scaffoldingWorkChecklist: checklistChangeHandler(prevValue.scaffoldingWorkChecklist)
            };

            return updatedFormState;
        });
    }

    function closedSpaceChecklistChangeHandler(
        checklistChangeHandler: (prevValue: ClosedSpaceChecklistFormState) => ClosedSpaceChecklistFormState
    ) {
        onChange((prevValue) => {
            const updatedFormState: RiskIdentificationsFormState = {
                ...prevValue,
                closedSpaceChecklist: checklistChangeHandler(prevValue.closedSpaceChecklist)
            };

            return updatedFormState;
        });
    }

    function asbestosChecklistChangeHandler(
        checklistChangeHandler: (prevValue: AsbestosChecklistFormState) => AsbestosChecklistFormState
    ) {
        onChange((prevValue) => {
            const updatedFormState: RiskIdentificationsFormState = {
                ...prevValue,
                asbetestosChecklist: checklistChangeHandler(prevValue.asbetestosChecklist)
            };

            return updatedFormState;
        });
    }

    function hydrogenSulphideChecklistChangeHandler(
        checklistChangeHandler: (prevValue: HydrogenSulphideChecklistFormState) => HydrogenSulphideChecklistFormState
    ) {
        onChange((prevValue) => {
            const updatedFormState: RiskIdentificationsFormState = {
                ...prevValue,
                hydrogenSulphideChecklist: checklistChangeHandler(prevValue.hydrogenSulphideChecklist)
            };

            return updatedFormState;
        });
    }

    function beefGasChecklistChangeHandler(
        checklistChangeHandler: (prevValue: BeefGasChecklistFormState) => BeefGasChecklistFormState
    ) {
        onChange((prevValue) => {
            const updatedFormState: RiskIdentificationsFormState = {
                ...prevValue,
                beefGasChecklist: checklistChangeHandler(prevValue.beefGasChecklist)
            };

            return updatedFormState;
        });
    }

    function heavyLiftingChecklistChangeHandler(
        checklistChangeHandler: (prevValue: HeavyLiftingChecklistFormState) => HeavyLiftingChecklistFormState
    ) {
        onChange((prevValue) => {
            const updatedFormState: RiskIdentificationsFormState = {
                ...prevValue,
                heavyLiftingChecklist: checklistChangeHandler(prevValue.heavyLiftingChecklist)
            };

            return updatedFormState;
        });
    }
    function excavationAndPolingChecklistChangeHandler(
        checklistChangeHandler: (
            prevValue: ExcavationAndPolingChecklistFormState
        ) => ExcavationAndPolingChecklistFormState
    ) {
        onChange((prevValue) => {
            const updatedFormState: RiskIdentificationsFormState = {
                ...prevValue,
                excavationAndPolingChecklist: checklistChangeHandler(prevValue.excavationAndPolingChecklist)
            };

            return updatedFormState;
        });
    }
    function raftChecklistChangeHandler(
        checklistChangeHandler: (prevValue: RaftChecklistFormState) => RaftChecklistFormState
    ) {
        onChange((prevValue) => {
            const updatedFormState: RiskIdentificationsFormState = {
                ...prevValue,
                raftChecklist: checklistChangeHandler(prevValue.raftChecklist)
            };

            return updatedFormState;
        });
    }
    function divingChecklistChangeHandler(
        checklistChangeHandler: (prevValue: DivingChecklistFormState) => DivingChecklistFormState
    ) {
        onChange((prevValue) => {
            const updatedFormState: RiskIdentificationsFormState = {
                ...prevValue,
                divingChecklist: checklistChangeHandler(prevValue.divingChecklist)
            };

            return updatedFormState;
        });
    }

    const highlightRaftChecklist =
        formState.isRaftIncludedInPrint.value && !formState.raftChecklist.underWaterWorkPermitSequenceIdentifier.value;
    const highlightDivingChecklist =
        formState.isDivingIncludedInPrint.value && !formState.divingChecklist.divingPermitSequenceIdentifier.value;

    const scaffoldingConstructionChecklistVersion = dbRiskIdentifications?.scaffoldingConstructionChecklist.checklistVersion;
    const scaffoldingWorkChecklistVersion = dbRiskIdentifications?.scaffoldingWorkChecklist.checklistVersion;

    return (
        <>
            <h1 className="h6">{resources.get("RiskIdentification")}</h1>
            <ul className="list-unstyled">
                <li className="m-2">
                    <ClickableLabelCheckbox
                        id="is-work-at-height-included-in-print"
                        checked={formState.isWorkAtHeightIncludedInPrint.value}
                        label={resources.get("WorkAtHeight")}
                        onChange={(checked) => changeHandler("isWorkAtHeightIncludedInPrint", checked)}
                        onLabelClick={() => setShowWorkAtHeightChecklistModal(true)}
                        readOnly={!canManageContent}
                    />
                </li>
                {scaffoldingConstructionChecklistVersion === 0 && scaffoldingWorkChecklistVersion === 0 ? (
                    <li className="m-2">
                        <ClickableLabelCheckbox
                            id="is-scaffolding-included-in-print"
                            checked={formState.isScaffoldingIncludedInPrint.value}
                            label={resources.get("Scaffolding")}
                            onChange={(checked) => changeHandler("isScaffoldingIncludedInPrint", checked)}
                            onLabelClick={() => setShowScaffoldingChecklistModal(true)}
                            readOnly={!canManageContent}
                        />
                    </li>
                ) : null}
                {scaffoldingConstructionChecklistVersion !== 0 ? (
                    <li className="m-2">
                        <ClickableLabelCheckbox
                            id="scaffolding-construction"
                            checked={formState.scaffoldingConstruction.value}
                            label={resources.get("ScaffoldingConstruction")}
                            onChange={(checked) => changeHandler("scaffoldingConstruction", checked)}
                            onLabelClick={() => setShowScaffoldingConstructionChecklistModal(true)}
                            readOnly={!canManageContent}
                        />
                    </li>
                ) : null}
                {scaffoldingWorkChecklistVersion !== 0 ? (
                    <li className="m-2">
                        <ClickableLabelCheckbox
                            id="scaffolding-work"
                            checked={formState.scaffoldingWork.value}
                            label={resources.get("ScaffoldingWork")}
                            onChange={(checked) => changeHandler("scaffoldingWork", checked)}
                            onLabelClick={() => setShowScaffoldingWorkChecklistModal(true)}
                            readOnly={!canManageContent}
                        />
                    </li>
                ) : null}
                <li className="m-2">
                    <ClickableLabelCheckbox
                        id="is-closed-space-included-in-print"
                        checked={formState.isClosedSpaceIncludedInPrint.value}
                        label={resources.get("ClosedSpace")}
                        onChange={(checked) => changeHandler("isClosedSpaceIncludedInPrint", checked)}
                        onLabelClick={() => setShowClosedSpaceChecklistModal(true)}
                        readOnly={!canManageContent}
                    />
                </li>
                <li className="m-2">
                    <ClickableLabelCheckbox
                        id="is-asbestos-included-in-print"
                        checked={formState.isAsbestosIncludedInPrint.value}
                        label={resources.get("Asbestos")}
                        onChange={(checked) => changeHandler("isAsbestosIncludedInPrint", checked)}
                        onLabelClick={() => setShowAsbestosChecklistModal(true)}
                        readOnly={!canManageContent}
                    />
                </li>
                <li className="m-2">
                    <ClickableLabelCheckbox
                        id="is-hydrogen-sulphide-included-in-print"
                        checked={formState.isHydrogenSulphideIncludedInPrint.value}
                        label={resources.get("HydrogenSulphide")}
                        onChange={(checked) => changeHandler("isHydrogenSulphideIncludedInPrint", checked)}
                        onLabelClick={() => setShowHydrogenSulphideChecklistModal(true)}
                        readOnly={!canManageContent}
                    />
                </li>
                <li className="m-2">
                    <ClickableLabelCheckbox
                        id="is-beef-gas-included-in-print"
                        checked={formState.isBeefGasIncludedInPrint.value}
                        label={resources.get("BeefGas")}
                        onChange={(checked) => changeHandler("isBeefGasIncludedInPrint", checked)}
                        onLabelClick={() => setShowBeefGasChecklistModal(true)}
                        readOnly={!canManageContent}
                    />
                </li>
                <li className="m-2">
                    <ClickableLabelCheckbox
                        id="is-heavy-lifting-included-in-print"
                        checked={formState.isHeavyLiftingIncludedInPrint.value}
                        label={resources.get("HeavyLifting")}
                        onChange={(checked) => changeHandler("isHeavyLiftingIncludedInPrint", checked)}
                        onLabelClick={() => setShowHeavyLiftingChecklistModal(true)}
                        readOnly={!canManageContent}
                    />
                </li>
                <li className="m-2">
                    <ClickableLabelCheckbox
                        id="is-excavation-and-poling-included-in-print"
                        checked={formState.isExcavationAndPolingIncludedInPrint.value}
                        label={resources.get("ExcavationAndPoling")}
                        onChange={(checked) => changeHandler("isExcavationAndPolingIncludedInPrint", checked)}
                        onLabelClick={() => setShowExcavationAndPolingChecklistModal(true)}
                        readOnly={!canManageContent}
                    />
                </li>
                <li className="m-2 d-flex">
                    {highlightRaftChecklist ? (
                        <OverlayTrigger
                            overlay={<Tooltip>{resources.get("RaftChecklist_UnderWaterWorkPermitMissing")}</Tooltip>}
                            placement="top"
                            delay={{ show: 200, hide: 0 }}
                        >
                            <span className="me-1">
                                <i className="fas fa-circle-info text-danger"></i>
                            </span>
                        </OverlayTrigger>
                    ) : null}
                    <ClickableLabelCheckbox
                        id="is-raft-included-in-print"
                        checked={formState.isRaftIncludedInPrint.value}
                        label={resources.get("Raft")}
                        onChange={(checked) => changeHandler("isRaftIncludedInPrint", checked)}
                        onLabelClick={() => setShowRaftChecklistModal(true)}
                        readOnly={!canManageContent}
                        labelClassName={highlightRaftChecklist ? "text-danger" : ""}
                    />
                </li>
                <li className="m-2 d-flex">
                    {highlightDivingChecklist ? (
                        <OverlayTrigger
                            overlay={<Tooltip>{resources.get("DivingChecklist_DivingPermitMissing")}</Tooltip>}
                            placement="top"
                            delay={{ show: 200, hide: 0 }}
                        >
                            <span className="me-1">
                                <i className="fas fa-circle-info text-danger"></i>
                            </span>
                        </OverlayTrigger>
                    ) : null}
                    <ClickableLabelCheckbox
                        id="is-diving-included-in-print"
                        checked={formState.isDivingIncludedInPrint.value}
                        label={resources.get("Diving")}
                        onChange={(checked) => changeHandler("isDivingIncludedInPrint", checked)}
                        onLabelClick={() => setShowDivingChecklistModal(true)}
                        readOnly={!canManageContent}
                        labelClassName={highlightDivingChecklist ? "text-danger" : ""}
                    />
                </li>
            </ul>
            <WorkAtHeightChecklistModal
                checklistVersion={
                    dbRiskIdentifications?.workAtHeightChecklist.checklistVersion ??
                    WORK_AT_HEIGHT_CHECKLIST_VERSION_2026_02
                }
                formState={formState.workAtHeightChecklist}
                onChange={workAtHeightChecklistChangeHandler}
                resources={resources}
                show={showWorkAtHeightChecklistModal}
                onHide={() => setShowWorkAtHeightChecklistModal(false)}
                canManageContent={canManageContent}
            />
            <ScaffoldingChecklistModal
                formState={formState.scaffoldingChecklist}
                onChange={scaffoldingChecklistChangeHandler}
                resources={resources}
                show={showScaffoldingChecklistModal}
                onHide={() => setShowScaffoldingChecklistModal(false)}
                canManageContent={canManageContent}
            />
            <ScaffoldingConstructionChecklistModal
                formState={formState.scaffoldingConstructionChecklist}
                onChange={scaffoldingConstructionChecklistChangeHandler}
                resources={resources}
                show={showScaffoldingConstructionChecklistModal}
                onHide={() => setShowScaffoldingConstructionChecklistModal(false)}
                canManageContent={canManageContent}
            />
            <ScaffoldingWorkChecklistModal
                formState={formState.scaffoldingWorkChecklist}
                onChange={scaffoldingWorkChecklistChangeHandler}
                resources={resources}
                show={showScaffoldingWorkChecklistModal}
                onHide={() => setShowScaffoldingWorkChecklistModal(false)}
                canManageContent={canManageContent}
            />
            <ClosedSpaceChecklistModal
                checklistVersion={
                    dbRiskIdentifications?.closedSpaceChecklist.checklistVersion ??
                    CLOSED_SPACE_CHECKLIST_VERSION_2026_02
                }
                formState={formState.closedSpaceChecklist}
                onChange={closedSpaceChecklistChangeHandler}
                resources={resources}
                show={showClosedSpaceChecklistModal}
                onHide={() => setShowClosedSpaceChecklistModal(false)}
                canManageContent={canManageContent}
            />
            <AsbestosChecklistModal
                formState={formState.asbetestosChecklist}
                onChange={asbestosChecklistChangeHandler}
                resources={resources}
                show={showAsbestosChecklistModal}
                onHide={() => setShowAsbestosChecklistModal(false)}
                canManageContent={canManageContent}
                checklistVersion={
                    dbRiskIdentifications?.asbetestosChecklist.checklistVersion ?? ASBESTOS_CHECKLIST_VERSION_2026_02
                }
            />
            <HydrogenSulphideChecklistModal
                formState={formState.hydrogenSulphideChecklist}
                onChange={hydrogenSulphideChecklistChangeHandler}
                onHide={() => setShowHydrogenSulphideChecklistModal(false)}
                resources={resources}
                show={showHydrogenSulphideChecklistModal}
                canManageContent={canManageContent}
                checklistVersion={
                    dbRiskIdentifications?.hydrogenSulphideChecklist.checklistVersion ??
                    HYDROGEN_SULPHIDE_CHECKLIST_VERSION_2026_02
                }
            />
            <BeefGasChecklistModal
                checklistVersion={
                    dbRiskIdentifications?.beefGasChecklist.checklistVersion ?? BEEF_GAS_CHECKLIST_VERSION_2026_02
                }
                formState={formState.beefGasChecklist}
                onChange={beefGasChecklistChangeHandler}
                onHide={() => setShowBeefGasChecklistModal(false)}
                show={showBeefGasChecklistModal}
                resources={resources}
                canManageContent={canManageContent}
            />
            <HeavyLiftingChecklistModal
                checklistVersion={
                    dbRiskIdentifications?.heavyLiftingChecklist.checklistVersion ??
                    HEAVY_LIFTING_CHECKLIST_VERSION_2026_02
                }
                formState={formState.heavyLiftingChecklist}
                onChange={heavyLiftingChecklistChangeHandler}
                onHide={() => setShowHeavyLiftingChecklistModal(false)}
                show={showHeavyLiftingChecklistModal}
                resources={resources}
                baseUserInfo={baseUserInfo}
                craneOperatorSignatureManager={craneOperatorSignatureManager}
                canManageContent={canManageContent}
            />
            <ExcavationAndPolingChecklistModal
                checklistVersion={
                    dbRiskIdentifications?.excavationAndPolingChecklist.checklistVersion ??
                    EXCAVATION_AND_POLING_CHECKLIST_VERSION_2026_02
                }
                formState={formState.excavationAndPolingChecklist}
                onChange={excavationAndPolingChecklistChangeHandler}
                onHide={() => setShowExcavationAndPolingChecklistModal(false)}
                resources={resources}
                show={showExcavationAndPolingChecklistModal}
                canManageContent={canManageContent}
            />
            <RaftChecklistModal
                formState={formState.raftChecklist}
                onChange={raftChecklistChangeHandler}
                onHide={() => setShowRaftChecklistModal(false)}
                show={showRaftChecklistModal}
                resources={resources}
                canCreateUnderWaterWorkPermits={canCreateUnderWaterWorkPermits}
                canManageContent={canManageContent}
            />
            <DivingChecklistModal
                formState={formState.divingChecklist}
                onChange={divingChecklistChangeHandler}
                onHide={() => setShowDivingChecklistModal(false)}
                show={showDivingChecklistModal}
                resources={resources}
                canCreateDivingPermits={canCreateDivingPermits}
                canManageContent={canManageContent}
            />

            {craneOperatorSignatureManager ? (
                <SignatureModal
                    resources={resources}
                    SignatureManager={craneOperatorSignatureManager}
                    ref={craneOperatorSignatureManager.signaturePadRef}
                />
            ) : null}
        </>
    );
}

export default RiskIdentifications;
